
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ConnectionRequest {
  id: string;
  review_id: string;
  requester_email: string;
  requester_user_id?: string;
  terms_agreed: boolean;
  admin_notified: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'contacted';
  created_at: string;
  updated_at: string;
  review?: {
    id: string;
    company: string;
    job_title: string;
    unique_ref: string;
    user_id?: string;
  };
}

export const useConnectionRequests = () => {
  const [requests, setRequests] = useState<ConnectionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('review_connections')
        .select(`
          *,
          review:reviews(
            id,
            company,
            job_title,
            unique_ref,
            user_id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type the data properly to match our interface
      const typedRequests: ConnectionRequest[] = (data || []).map(item => ({
        id: item.id,
        review_id: item.review_id,
        requester_email: item.requester_email,
        requester_user_id: item.requester_user_id,
        terms_agreed: item.terms_agreed,
        admin_notified: item.admin_notified,
        status: item.status as 'pending' | 'approved' | 'rejected' | 'contacted',
        created_at: item.created_at,
        updated_at: item.updated_at,
        review: item.review ? {
          id: item.review.id,
          company: item.review.company,
          job_title: item.review.job_title,
          unique_ref: item.review.unique_ref,
          user_id: item.review.user_id
        } : undefined
      }));
      
      setRequests(typedRequests);
    } catch (error) {
      console.error('Error fetching connection requests:', error);
      toast({
        title: "Error",
        description: "Failed to load connection requests.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const approveRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('review_connections')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, status: 'approved' as const, updated_at: new Date().toISOString() }
          : req
      ));

      toast({
        title: "Request Approved",
        description: "The connection request has been approved. Email notifications will be sent to both parties.",
      });
    } catch (error) {
      console.error('Error approving request:', error);
      toast({
        title: "Error",
        description: "Failed to approve request.",
        variant: "destructive"
      });
    }
  };

  const rejectRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('review_connections')
        .update({ 
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, status: 'rejected' as const, updated_at: new Date().toISOString() }
          : req
      ));

      toast({
        title: "Request Rejected",
        description: "The connection request has been rejected.",
      });
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast({
        title: "Error",
        description: "Failed to reject request.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  return {
    requests,
    isLoading,
    fetchRequests,
    approveRequest,
    rejectRequest
  };
};
